/**
 * Data types for hooks and components
 * Centralized type definitions for bucket, position, trade, and price data
 */

import { RawBucket, OrderType, MultiFeedBucketData, RawOrder, RawPosition, MultiFeedPositionsData, RawTrade } from "./api";
import {
  toNumberFromSatoshi,
  parseNumericValue,
  toBigInt,
  fromSatoshi,
} from "../utils/math";
import { normalizeProtobufTimestamp, normalizeTimestamp } from "../utils/time";
import type { SubscriptionClient } from "../api/subscription/SubscriptionClient";
import {
  ORDER_SIDE_LONG,
  ORDER_SIDE_SHORT,
  MARGIN_MODE_CROSS,
} from "../../components/smartlayout/constants";

// ============================================================================
// BUCKET TYPES
// ============================================================================

/**
 * Configuration for bucket collections hook
 */
export interface BucketCollectionsConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  walletAddress: string;
  authToken?: string;
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string | number; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

/**
 * Full bucket data structure
 * Contains all bucket information from API
 */
export interface Bucket {
  bucketId: string;
  address: string;
  marginType: string; // e.g., "cross", "isolated"
  collateralAsset: string; // e.g., "USDC"
  depositedMargin: number; // Total margin deposited
  totalEquity: number;
  availableMargin: number;
  usedMargin: number;
  unrealizedProfit: number;
  unrealizedLoss: number;
  realizedProfit: number;
  realizedLoss: number;
  unrealizedPnL: number; // Calculated: unrealizedProfit - unrealizedLoss
  marginRatio: number;
  imr: number; // Initial Margin Requirement (as number, e.g., 10 for 10%)
  totalNotionalValue: number;
  riskLevel: string;
  maxLeverage: number;
  correlationOffset: number;
  status: string; // e.g., "dormant", "active"
  createdAt: string; // ISO timestamp string
  updatedAt: string; // ISO timestamp string
  // Legacy/computed fields for backward compatibility
  leverage: number; // Alias for maxLeverage
  marginMode: string; // Alias for marginType
  raw: any;
}

/**
 * Configuration for buckets hook
 */
export interface BucketsConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  walletAddress: string;
  authToken: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket: boolean; // Use WebSocket as primary source (default: true)
  marketId: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

// ============================================================================
// POSITION TYPES
// ============================================================================

/**
 * Position data structure
 * Represents a trading position
 */
export interface Position {
  positionId: string; // Position identifier (from API position_id field)
  marketId: string;
  bucketId: string; // Bucket ID associated with the position
  marginMode: string; // e.g., 'CROSS', 'ISOLATED'
  symbol: string; // May not be in API response, will need to map from marketId
  size: number; // Decimal value
  entryPrice: number; // Decimal value
  markPrice: number; // Decimal value
  liquidationPrice: number; // Decimal value
  unrealizedPnL: number; // Decimal value
  leverage: number; // Decimal value
  margin: number; // Calculated or from API
  side: "LONG" | "SHORT"; // Inferred from size sign
  raw?: any; // Store raw data for modal display
}

/**
 * Configuration for positions hook
 */
export interface PositionsConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  authToken?: string;
  walletAddress?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
  symbol?: string; // Optional symbol for multi-feed subscription (default: "USDC/USDC" for user-global)
}

// ============================================================================
// TRADE PRICE TYPES
// ============================================================================

/**
 * Last trade price data
 * Represents the most recent trade execution
 */
export interface LastPriceData {
  market_id: string;
  price: number;
  quantity: number;
  timestamp: number;
  is_buyer_maker: boolean;
  trade_id: string;
}

/**
 * Mark price data
 * Represents the current mark price for a symbol
 */
export interface MarkPriceData {
  symbol: string;
  mark_price: number; // Mark price as decimal number (converted from satoshi)
  timestamp: number;
}

/**
 * Configuration for trade price hook
 * Symbol is derived from marketId using useMarkets hook
 */
export interface TradePriceConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  marketId: string;
  limit?: number;
  disablePolling?: boolean; // If true, disable HTTP polling fallback when WebSocket is disconnected
}

/**
 * Props for TradePrice component
 * subscriptionClient is required; other fields are optional with defaults provided by the component
 * Symbol is derived from marketId using useMarkets hook
 */
export interface TradePriceProps {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL?: string;
  marketId?: string;
  limit?: number;
}

/**
 * Recent trade data for display in trade table
 */
export interface RecentTradeDos {
  time: number;
  size: number;
  price: number;
  is_buyer_maker: boolean;
}

/**
 * Transform RawTrade array to RecentTradeDos array for display
 * Converts satoshi strings to decimal numbers and normalizes timestamps
 * Sorts trades by timestamp descending (newest first)
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformRawTradesToRecentTradeDos(
  rawTrades: RawTrade[],
): RecentTradeDos[] {
  if (!rawTrades || !Array.isArray(rawTrades) || rawTrades.length === 0) {
    return [];
  }

  // Sort by timestamp descending (newest first)
  const sortedTrades = [...rawTrades].sort((a, b) => {
    const timeA = normalizeTimestamp(a.timestamp);
    const timeB = normalizeTimestamp(b.timestamp);
    return timeB - timeA; // Descending: newest first
  });

  // Convert to display format
  return sortedTrades.map((trade: RawTrade): RecentTradeDos => {
    // Handle timestamp - convert string to number and ensure milliseconds
    const timestamp = normalizeTimestamp(
      trade.timestamp as string | number,
    );

    // Convert satoshi strings to decimal numbers
    const size: number = toNumberFromSatoshi(trade.quantity);
    const price: number = toNumberFromSatoshi(trade.price);

    return {
      time: timestamp,
      size,
      price,
      // is_buyer_maker is not in RawTrade interface, default to false
      // If needed, this should come from WebSocket data or be calculated from buy_address/sell_address
      is_buyer_maker: false,
    };
  });
}

// ============================================================================
// ORDER TYPES
// ============================================================================

/**
 * Pending order data structure
 * Represents an order that is pending, open, or partially filled
 * Transformed from RawOrder for use in hooks and components
 */
export interface PendingOrder {
  id: string;
  orderId: string;
  marketId: string;
  side: "BUY" | "SELL" | "LONG" | "SHORT";
  type: OrderType;
  quantity: number;
  price: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  timeInForce: string;
  createdAt: number;
  updatedAt: number;
  raw?: any;
}

/**
 * Configuration for pending orders hook
 */
export interface PendingOrdersConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  authToken?: string;
  walletAddress?: string; // Optional - if not provided, API may derive from auth token
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  marketId?: string; // Optional market ID for multi-feed subscription (default: "0" for user-global)
}

/**
 * Transform API/WS response to Position format
 * Converts Satoshi strings to decimal numbers for display
 * Handles both RawPosition (from HTTP API) and MultiFeedPositionsData (from WebSocket)
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformPosition(
  pos: RawPosition | MultiFeedPositionsData,
): Position {
  // Normalize position data - handle both RawPosition and MultiFeedPositionsData
  // positionId is required, so generate a fallback if missing
  let positionId: string;
  if ("position_id" in pos && pos.position_id) {
    // RawPosition always has position_id
    positionId = pos.position_id;
  } else if (
    "position_id" in pos &&
    (pos as MultiFeedPositionsData).position_id
  ) {
    // MultiFeedPositionsData might have position_id
    positionId = (pos as MultiFeedPositionsData).position_id!;
  } else {
    // Generate fallback ID if missing (for WebSocket updates without position_id)
    positionId = `${pos.market_id}-${pos.bucket_id || "default"}-${pos.address}`;
  }

  // Get entry price - RawPosition uses average_entry_price, MultiFeedPositionsData uses entry_price
  const entryPriceStr =
    "average_entry_price" in pos
      ? pos.average_entry_price
      : pos.entry_price;

  // Get side - RawPosition uses side (string), MultiFeedPositionsData uses position_side
  let side: "LONG" | "SHORT";
  if ("position_side" in pos && pos.position_side) {
    // MultiFeedPositionsData format
    side = pos.position_side;
  } else if ("side" in pos && pos.side) {
    // RawPosition format: "SIDE_BUY" (long) or "SIDE_SELL" (short)
    side =
      pos.side === "SIDE_BUY" || pos.side.toUpperCase().includes("BUY")
        ? ORDER_SIDE_LONG
        : ORDER_SIDE_SHORT;
  } else {
    // Fallback: infer from size sign
    const size = toNumberFromSatoshi(pos.size);
    side = size >= 0 ? ORDER_SIDE_LONG : ORDER_SIDE_SHORT;
  }

  // Convert size
  const size = toNumberFromSatoshi(pos.size);

  // Convert numeric fields from Satoshi strings to decimals
  const entryPrice = toNumberFromSatoshi(entryPriceStr);
  const markPrice = toNumberFromSatoshi(pos.mark_price);
  const leverage = pos.leverage
    ? toNumberFromSatoshi(pos.leverage)
    : 0;

  // Calculate unrealized PnL from API format (unrealized_profit - unrealized_loss)
  // or use legacy format (unrealized_pnl) if new fields are not available
  // Use BigInt for precision during subtraction
  let unrealizedPnL = 0;
  if (
    pos.unrealized_profit !== undefined ||
    pos.unrealized_loss !== undefined
  ) {
    // New API format: separate profit and loss fields
    // Use BigInt for precise subtraction
    const profitBigInt = toBigInt(pos.unrealized_profit);
    const lossBigInt = toBigInt(pos.unrealized_loss);
    const pnlBigInt = profitBigInt - lossBigInt;
    // Convert BigInt result to decimal using utility function for consistency
    unrealizedPnL = toNumberFromSatoshi(pnlBigInt.toString());
  } else if ("unrealized_pnl" in pos && pos.unrealized_pnl !== undefined) {
    // Legacy format: single unrealized_pnl field (only in RawPosition)
    unrealizedPnL = toNumberFromSatoshi(pos.unrealized_pnl);
  }

  // Calculate margin (margin_balance from API or calculate: size * entryPrice / leverage)
  const marginBalance = toNumberFromSatoshi(pos.margin_balance);
  const margin =
    marginBalance ||
    (leverage && entryPrice && size !== 0
      ? Math.abs((Math.abs(size) * entryPrice) / leverage)
      : 0);

  // Get margin mode - RawPosition has margin_mode/margin_type, MultiFeedPositionsData might not
  const marginMode =
    ("margin_mode" in pos && pos.margin_mode) ||
    ("margin_type" in pos && pos.margin_type) ||
    MARGIN_MODE_CROSS;

  return {
    positionId,
    marketId: pos.market_id.toString(),
    symbol: pos.symbol || `m-${pos.market_id}`,
    size: Math.abs(size),
    entryPrice,
    markPrice,
    liquidationPrice: pos.liquidation_price
      ? toNumberFromSatoshi(pos.liquidation_price)
      : 0,
    unrealizedPnL,
    leverage,
    margin,
    side,
    marginMode,
    bucketId: pos.bucket_id ? String(pos.bucket_id) : "",
    raw: pos,
  };
}

/**
 * Transform RawOrder from API/WS response to PendingOrder format
 * Handles RawOrder structure from API (snake_case with enum values and string numbers)
 * Based on RawOrder interface from src/sdk/types/api.ts
 * Requires order_id to be present (orders without order_id should be filtered before calling this)
 * This is a pure function with no dependencies, suitable for use outside React hooks
 */
export function transformRawOrderToPendingOrder(order: RawOrder): PendingOrder {
  // Normalize side enum: "ORDER_SIDE_BUY" -> "BUY", "ORDER_SIDE_SELL" -> "SELL"
  const normalizedSide = order.side === "SIDE_BUY" ? "BUY" : "SELL";

  // Pass through type as-is (no transformation needed)
  // RawOrder.type is already OrderType: "ORDER_TYPE_LIMIT", "ORDER_TYPE_MARKET", etc.

  // Normalize status enum: "ORDER_STATUS_OPEN" -> "OPEN", etc.
  // RawOrder.status is OrderStatus type: "ORDER_STATUS_OPEN", "ORDER_STATUS_PARTIAL", "ORDER_STATUS_CANCELLED", etc.
  let normalizedStatus: string = order.status || "ORDER_STATUS_PENDING";
  if (normalizedStatus.includes("ORDER_STATUS_")) {
    normalizedStatus = normalizedStatus.replace("ORDER_STATUS_", "");
  }
  // Normalize timeInForce enum: "TIME_IN_FORCE_GTC" -> "GTC"
  // RawOrder.time_in_force is string like "TIME_IN_FORCE_GTC"
  const rawTimeInForce = order.time_in_force;
  let normalizedTimeInForce: string | undefined = rawTimeInForce;
  if (rawTimeInForce && rawTimeInForce.includes("TIME_IN_FORCE_")) {
    normalizedTimeInForce = rawTimeInForce.replace("TIME_IN_FORCE_", "");
  }

  // Convert string numbers to numbers
  // RawOrder has price, quantity, filled_quantity as strings (Satoshi format)
  // Use utility function from math.ts for consistent satoshi conversion
  const parseNumber = (value: string | number | undefined): number =>
    toNumberFromSatoshi(value);

  // Use RawOrder fields (snake_case)
  const quantity = parseNumber(order.quantity);
  const price = parseNumber(order.price);
  const filledQuantity = parseNumber(order.filled_quantity);
  const remainingQuantity = quantity - filledQuantity;

  // Convert timestamp to number (milliseconds)
  // RawOrder.timestamp can be Timestamp object (with seconds/nanos) or string/number
  // PendingOrder requires createdAt and updatedAt as numbers (not undefined)
  // Use normalizeProtobufTimestamp utility which handles all timestamp formats
  const timestamp = normalizeProtobufTimestamp(order.timestamp, Date.now());
  return {
    id: order.order_id,
    orderId: order.order_id,
    marketId: order.market_id || order.bucket_id,
    side: normalizedSide,
    type: order.type,
    quantity,
    price,
    filledQuantity,
    remainingQuantity,
    status: normalizedStatus,
    timeInForce: normalizedTimeInForce,
    createdAt: timestamp,
    updatedAt: timestamp, // Use same timestamp if updated_at is not available
    raw: order,
  };
}

// ============================================================================
// TRADE OPERATION TYPES
// ============================================================================

/**
 * Configuration for trade operation hook
 */
export interface TradeOperationConfig {
  baseURL: string;
  mnemonic: string;
  marketId: string;
  marketSymbol?: string;
  selectedBucketBalance?: number; // Balance from selected bucket (nominalValue)
  selectedBucketId?: string; // Bucket ID for order placement (required for placeOrder)
  tradePrice?: number; // Trade price from useTradePrice hook for market orders
}

// ============================================================================
// ORDERBOOK TYPES
// ============================================================================

/**
 * Individual orderbook level (price, size, cumulative depth)
 */
export interface OrderbookLevel {
  price: number;
  size: number;
  cumulative: number;
  flash?: "up" | "down" | null; // Visual indicator for changed levels
}
export type FastNumberLevel = [number, number][];
/**
 * Complete orderbook data structure
 */
export interface OrderbookData {
  asks: OrderbookLevel[];
  bids: OrderbookLevel[];
  spread: number;
  midPrice: number;
  market_id: string;
  sequence_id: string;
  first_update_id: number;
  last_update_id: number;
  checksum: string;
  timestamp: string;
  snapshot?: boolean;
}

/**
 * Configuration for orderbook hook
 */
export interface OrderbookConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  marketId: string;
  maxDepth?: number;
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
  disablePolling?: boolean; // If true, disable HTTP polling fallback when WebSocket is disconnected
}

/**
 * Trade operation result
 */
export interface TradeResult {
  success: boolean;
  orderId?: string;
  error?: string;
  message?: string;
}

// ============================================================================
// FUNDING RATE TYPES
// ============================================================================

/**
 * Configuration for rates hook
 * Automatically subscribes to all rate sub-channels: funding_rate, deviation, twap
 */
export interface RateConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  baseURL: string;
  marketId: string; // Single market ID to subscribe to
  historyDepth?: number; // Number of historical updates to retain (default: 1 for latest only)
}

/**
 * Funding rate data structure
 */
export interface FundingRateData {
  symbol: string;
  market_id: number;
  market_type: string;
  funding_rate: string;
  mark_price: string;
  index_price: string;
  premium_rate: string;
  ema_funding_rate: string;
  raw_funding_rate: string;
  calculation_time_unix: number;
  next_funding_time_unix: number;
  funding_interval_seconds: number;
  calculation_method: string;
  is_valid: boolean;
  is_stale: boolean;
  calculation_details?: Record<string, string>;
  // Options-specific (optional):
  option_type?: string;
  strike_price?: string;
  mark_iv?: string;
  time_value?: string;
  intrinsic_value?: string;
  funding_premium?: string;
  raw?: any; // Store raw data for modal display
}

// ============================================================================
// KLINE TYPES
// ============================================================================

/**
 * Configuration for kline hook
 */
export interface KlineConfig {
  subscriptionClient: SubscriptionClient; // SubscriptionClient instance (from context or provided externally) - required
  marketId: string;
  interval: string; // "1m", "5m", "15m", "1h", "4h", "1d"
  symbol?: string; // Symbol for kline subscription (e.g., "BTC/USDC"), defaults to "BTC/USDC" if not provided
  maxDataPoints?: number; // Maximum number of data points to keep in memory (default: 100)
}

/**
 * Kline (OHLC candlestick) data structure
 */
export interface KlineData {
  market_id: string;
  symbol?: string;
  interval: string;
  open_time: number;
  close_time: number;
  open: number; // Decimal value
  high: number; // Decimal value
  low: number; // Decimal value
  close: number; // Decimal value
  volume: number; // Decimal value
  trade_count?: number;
  timestamp: number;
  raw?: any; // Store raw data for modal display
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

import { timestampToISOString } from "../utils/time";

/**
 * Convert MultiFeedBucketData to RawBucket format
 * MultiFeedBucketData has fewer fields, so we provide defaults for missing fields
 * This is a pure function with no dependencies, suitable for use outside React hooks
 * @param multiFeedBucket - MultiFeedBucketData from WebSocket subscription
 * @returns RawBucket object with all required fields
 */
export function convertMultiFeedToRawBucket(
  multiFeedBucket: MultiFeedBucketData,
): RawBucket {
  const now = new Date().toISOString();
  return {
    bucket_id: multiFeedBucket.bucket_id,
    address: multiFeedBucket.address,
    margin_type: multiFeedBucket.margin_type.toLowerCase(),
    collateral_asset: "USDC", // Default, not provided in MultiFeedBucketData
    deposited_margin: "0", // Default, not provided in MultiFeedBucketData
    total_equity: multiFeedBucket.total_equity,
    available_margin: multiFeedBucket.available_margin,
    used_margin: multiFeedBucket.used_margin,
    unrealized_profit: multiFeedBucket.unrealized_profit,
    unrealized_loss: multiFeedBucket.unrealized_loss, // Default, not provided in MultiFeedBucketData
    realized_profit: "0", // Default, not provided in MultiFeedBucketData
    realized_loss: "0", // Default, not provided in MultiFeedBucketData
    margin_ratio: "0", // Default, not provided in MultiFeedBucketData
    imr: "0", // Default, not provided in MultiFeedBucketData
    total_notional_value: "0", // Default, not provided in MultiFeedBucketData
    risk_level: "low", // Default, not provided in MultiFeedBucketData
    max_leverage: "0", // Default, not provided in MultiFeedBucketData
    correlation_offset: 0, // Default, not provided in MultiFeedBucketData
    status: "active", // Default, not provided in MultiFeedBucketData
    created_at: now, // Default, not provided in MultiFeedBucketData
    updated_at: now, // Default, not provided in MultiFeedBucketData
  };
}

/**
 * Convert raw API bucket data (snake_case) to Bucket type
 * Handles conversion from satoshi strings to decimal numbers
 * @param rawBucket - Raw bucket data from API (snake_case format)
 * @returns Bucket object with all fields properly converted
 */
export function transformRawBucketToBucket(rawBucket: RawBucket): Bucket {
  // Extract bucketId from RawBucket (always snake_case)
  const bucketId = rawBucket.bucket_id || "---";

  // Calculate unrealized PnL using BigInt for precision
  // Convert satoshi strings to BigInt, subtract, then convert result to decimal
  const unrealizedProfitBigInt = toBigInt(rawBucket.unrealized_profit);
  const unrealizedLossBigInt = toBigInt(rawBucket.unrealized_loss);
  const unrealizedPnLBigInt = unrealizedProfitBigInt - unrealizedLossBigInt;

  // Convert BigInt result to decimal number using fromSatoshi (preserves precision)
  const unrealizedPnL = fromSatoshi(unrealizedPnLBigInt.toString());

  // Also convert individual profit/loss values for the Bucket interface
  const unrealizedProfit = toNumberFromSatoshi(rawBucket.unrealized_profit);
  const unrealizedLoss = toNumberFromSatoshi(rawBucket.unrealized_loss);

  // Parse max leverage - leverage values are not in satoshi format, they're decimal numbers
  // Uses utility function from math.ts for consistency
  const maxLeverage = parseNumericValue(rawBucket.max_leverage);

  return {
    bucketId,
    address: rawBucket.address || "",
    marginType: rawBucket.margin_type || "cross",
    collateralAsset: rawBucket.collateral_asset || "",
    depositedMargin: toNumberFromSatoshi(rawBucket.deposited_margin),
    totalEquity: toNumberFromSatoshi(rawBucket.total_equity),
    availableMargin: toNumberFromSatoshi(rawBucket.available_margin),
    usedMargin: toNumberFromSatoshi(rawBucket.used_margin),
    unrealizedProfit,
    unrealizedLoss,
    realizedProfit: toNumberFromSatoshi(rawBucket.realized_profit),
    realizedLoss: toNumberFromSatoshi(rawBucket.realized_loss),
    unrealizedPnL,
    marginRatio: toNumberFromSatoshi(rawBucket.margin_ratio),
    imr: toNumberFromSatoshi(rawBucket.imr), // IMR is not satoshi-scaled, it's a percentage
    totalNotionalValue: toNumberFromSatoshi(rawBucket.total_notional_value),
    riskLevel: rawBucket.risk_level || "",
    maxLeverage,
    correlationOffset: rawBucket.correlation_offset || 0,
    status: rawBucket.status || "",
    createdAt: timestampToISOString(rawBucket.created_at),
    updatedAt: timestampToISOString(rawBucket.updated_at),
    // Legacy/computed fields for backward compatibility
    leverage: maxLeverage,
    marginMode: rawBucket.margin_type || "cross",
    raw: rawBucket,
  };
}

/**
 * Calculate risk ratio for a bucket (used margin / total equity)
 * Returns a value between 0 and 1
 */
export function calculateRiskRatio(bucket: Bucket): number {
  return bucket.totalEquity > 0
    ? Math.min(Math.max(bucket.usedMargin / bucket.totalEquity, 0), 1)
    : 0;
}

/**
 * Calculate cross margin ratio for a bucket (used margin / total equity)
 */
export function calculateCrossMarginRatio(bucket: Bucket): number {
  return bucket.totalEquity > 0 ? bucket.usedMargin / bucket.totalEquity : 0;
}

/**
 * Get display margin type name from bucket
 */
export function getDisplayMarginType(bucket: Bucket): string {
  const marginTypeValue = bucket.marginMode || bucket.marginType || "cross";
  return marginTypeValue === "ISOLATED" || marginTypeValue === "isolated"
    ? "ISOLATED"
    : "CROSS";
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if a bucket is RawBucket
 */
export function isRawBucket(
  bucket: RawBucket | MultiFeedBucketData,
): bucket is RawBucket {
  return "collateral_asset" in bucket;
}

/**
 * Type guard for LastPriceData
 */
export function isLastPriceData(value: any): value is LastPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.price === "number" &&
    typeof value.market_id === "string" &&
    typeof value.quantity === "number" &&
    typeof value.timestamp === "number" &&
    typeof value.is_buyer_maker === "boolean" &&
    typeof value.trade_id === "string"
  );
}

/**
 * Type guard for MarkPriceData
 */
export function isMarkPriceData(value: any): value is MarkPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.symbol === "string" &&
    typeof value.mark_price === "number" &&
    typeof value.timestamp === "number"
  );
}
